/**
 * <dendro-scroll-nav> — dots + arrows for any horizontal scroller.
 *
 * Placement rule: the element must be a SIBLING that FOLLOWS the scroller, never a child of it.
 * A scroller is a flex row, so a nav nested inside becomes just another flex item and lands
 * between the cards (that bug shipped once; hence this note).
 *
 * Target resolution: `data-target` selector within the same parent, else previousElementSibling.
 * Items are the scroller's *rendered* children — Shopify blocks emit <link>/<script> siblings
 * alongside the real markup, so those are filtered out or the dot count lies.
 *
 * Layout discipline (2026-08-06, PSI attributed ~163ms of forced reflow here): every layout
 * read is batched and cached, never interleaved with a DOM write.
 *   - build() reads the item list BEFORE clearing/appending, and appends via a fragment.
 *   - measure() is the ONLY place that reads geometry; it caches step + maxScroll.
 *   - sync() reads one cheap scrollLeft, then writes; scroll events are rAF-coalesced.
 * Geometry is re-measured on ResizeObserver, window resize, and when a
 * content-visibility:auto ancestor starts rendering (measurements are 0 while skipped).
 */
(function () {
  if (customElements.get('dendro-scroll-nav')) return;

  var IGNORED = { LINK: 1, SCRIPT: 1, STYLE: 1, TEMPLATE: 1, DIALOG: 1 };

  function realItems(scroller) {
    // One batched read pass: no writes happen between these, so the browser
    // recalculates style/layout once rather than once per child.
    return Array.prototype.filter.call(scroller.children, function (el) {
      if (IGNORED[el.tagName]) return false;
      if (!el.getClientRects().length) return false; // display:none (and 0-size) in one read
      return getComputedStyle(el).position !== 'fixed';
    });
  }

  class DendroScrollNav extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      var sel = this.getAttribute('data-target');
      this.scroller = sel
        ? (this.parentElement && this.parentElement.querySelector(sel))
        : this.previousElementSibling;
      if (!this.scroller) return;

      this.showArrows = this.getAttribute('data-arrows') === 'true';
      this.showDots = this.getAttribute('data-dots') !== 'false';
      // Optional prompt between the arrows ("Swipe for more"). Deliberately not interactive —
      // the arrows are the control. Absent/blank attribute renders nothing at all.
      this.labelText = (this.getAttribute('data-label') || '').trim();

      this.build();
      this.measure();
      this.sync();

      this._onScroll = this.requestSync.bind(this);
      this._onResize = this.remeasure.bind(this);
      this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
      if (typeof ResizeObserver === 'function') {
        // Width guard: RO fires repeatedly while the page loads (images resolving, layout
        // settling) and rebuild() runs realItems(), which is a getComputedStyle per child.
        // Nothing about the item geometry can change unless the scroller's width changed.
        this._ro = new ResizeObserver(function () {
          var w = this.scroller.clientWidth;
          if (w === this._lastWidth) return;
          this._lastWidth = w;
          this.rebuild();
        }.bind(this));
        this._ro.observe(this.scroller);
      }
      // content-visibility:auto keeps the subtree unrendered — every geometry read is 0 until
      // it starts rendering, so re-measure on the state change (see dendro-review-carousel).
      this._onCV = function (e) {
        if (!e.skipped) this.remeasure();
      }.bind(this);
      this.scroller.addEventListener('contentvisibilityautostatechange', this._onCV);
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    disconnectedCallback() {
      if (this.scroller && this._onScroll) {
        this.scroller.removeEventListener('scroll', this._onScroll);
        this.scroller.removeEventListener('contentvisibilityautostatechange', this._onCV);
      }
      if (this._ro) this._ro.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
      window.removeEventListener('resize', this._onResize);
    }

    build() {
      // Read the item list first — clearing then reading would force an extra layout.
      var items = realItems(this.scroller);
      var frag = document.createDocumentFragment();

      if (this.showArrows) {
        this.prev = this.arrow('prev', 'M15 5l-7 7 7 7');
        frag.appendChild(this.prev);
      }

      if (this.labelText) {
        this.label = document.createElement('span');
        this.label.className = 'dsn__label';
        // textContent, not innerHTML — the value comes from a merchant setting.
        this.label.textContent = this.labelText;
        frag.appendChild(this.label);
      }

      if (this.showDots) {
        this.dotsWrap = document.createElement('div');
        this.dotsWrap.className = 'dsn__dots';
        var self = this;
        this.dots = items.map(function (item, i) {
          var d = document.createElement('button');
          d.type = 'button';
          d.className = 'dsn__dot';
          d.setAttribute('aria-label', 'Go to item ' + (i + 1));
          d.addEventListener('click', function () {
            self.scroller.scrollTo({ left: item.offsetLeft - self.scroller.offsetLeft, behavior: 'smooth' });
          });
          self.dotsWrap.appendChild(d);
          return d;
        });
        frag.appendChild(this.dotsWrap);
      }

      if (this.showArrows) {
        this.next = this.arrow('next', 'M9 5l7 7-7 7');
        frag.appendChild(this.next);
      }

      this.items = items;
      this._activeIdx = -1;
      this.textContent = '';
      this.appendChild(frag);
    }

    /** The only geometry read path. Everything downstream consumes the cache. */
    measure() {
      var s = this.scroller;
      this._max = s.scrollWidth - s.clientWidth;
      if (this.items && this.items.length) {
        var gap = parseFloat(getComputedStyle(s).columnGap) || 0;
        this._step = this.items[0].getBoundingClientRect().width + gap;
      } else {
        this._step = s.clientWidth;
      }
      if (this._step < 1) this._step = 1;
    }

    remeasure() {
      this.measure();
      this.sync();
    }

    /** Coalesce scroll bursts into one read+write per frame. */
    requestSync() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(function () {
        this._raf = 0;
        this.sync();
      }.bind(this));
    }

    rebuild() {
      var count = realItems(this.scroller).length;
      if (this.items && count === this.items.length) return this.remeasure();
      this.build();
      this.remeasure();
    }

    arrow(dir, path) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'dsn__arrow dsn__arrow--' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? 'Previous' : 'Next');
      b.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">' +
        '<path d="' + path + '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      var self = this;
      b.addEventListener('click', function () {
        self.scroller.scrollBy({ left: (dir === 'prev' ? -1 : 1) * (self._step || self.scroller.clientWidth), behavior: 'smooth' });
      });
      return b;
    }

    sync() {
      // READ phase — scrollLeft only; extents come from the measure() cache.
      var left = this.scroller.scrollLeft;
      var max = this._max || 0;

      // WRITE phase.
      // Nothing to scroll = no chrome. Keeps the row from reserving dead space.
      this.hidden = max <= 2;
      if (this.hidden) return;

      if (this.dots && this.dots.length) {
        var idx = Math.round(left / this._step);
        idx = Math.max(0, Math.min(idx, this.dots.length - 1));
        if (idx !== this._activeIdx) {
          var prevIdx = this._activeIdx;
          this._activeIdx = idx;
          if (this.dots[prevIdx]) {
            this.dots[prevIdx].classList.remove('is-active');
            this.dots[prevIdx].setAttribute('aria-current', 'false');
          }
          this.dots[idx].classList.add('is-active');
          this.dots[idx].setAttribute('aria-current', 'true');
        }
      }
      if (this.prev) this.prev.disabled = left <= 2;
      if (this.next) this.next.disabled = left >= max - 2;
    }
  }

  customElements.define('dendro-scroll-nav', DendroScrollNav);
})();
